import { z } from "zod";

import { DbEnum } from "../../../../generated/prisma-enums";
import type { DataSchemaType } from "../../../../shared/domain/tools/schema-type";

/**
 * CRUD operations for document templates
 * Includes: List, Get, Create, Update, Delete
 */

/**
 * inputFormat stores a Record<string, DataSchemaType> — the template's variable schema.
 * We use z.custom to preserve the domain type end-to-end:
 *   Frontend (SchemaType) → Zod → API body → Prisma Json column
 */
const InputFormatSchema = z.custom<Record<string, DataSchemaType>>(
  (val) => typeof val === "object" && val !== null && !Array.isArray(val)
);

// ============================================================================
// LIST (paginated)
// ============================================================================

export const DocumentTemplateListParamsSchema = z.object({
  type: z.nativeEnum(DbEnum.DocumentTemplateType).optional(),
  status: z.nativeEnum(DbEnum.DocumentTemplateStatus).optional(),
  search: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  offset: z.coerce.number().int().min(0).optional()
});

// ============================================================================
// GET
// ============================================================================

export const DocumentTemplateGetPathVarsSchema = z.object({
  id: z.string()
});

// ============================================================================
// CREATE
// ============================================================================

export const DocumentTemplateCreateBodySchema = z.object({
  name: z.string().trim().min(1).max(255),
  description: z.string().optional(),
  type: z.nativeEnum(DbEnum.DocumentTemplateType),
  status: z.nativeEnum(DbEnum.DocumentTemplateStatus).optional(),
  htmlContent: z.string().optional(),
  fileUrl: z.string().optional(),
  previewFileUrl: z.string().optional(),
  inputFormat: InputFormatSchema.optional(),
  formatTemplateId: z.string().optional(),
  repairPptx: z.boolean().optional()
});

// ============================================================================
// UPDATE
// ============================================================================

export const DocumentTemplateUpdatePathVarsSchema = z.object({
  id: z.string()
});

export const DocumentTemplateUpdateBodySchema = z.object({
  name: z.string().trim().min(1).max(255).optional(),
  description: z.string().optional(),
  type: z.nativeEnum(DbEnum.DocumentTemplateType).optional(),
  status: z.nativeEnum(DbEnum.DocumentTemplateStatus).optional(),
  htmlContent: z.string().optional(),
  fileUrl: z.string().optional(),
  previewFileUrl: z.string().optional(),
  inputFormat: InputFormatSchema.optional(),
  formatTemplateId: z.string().nullable().optional(),
  repairPptx: z.boolean().optional()
});

// ============================================================================
// DELETE
// ============================================================================

export const DocumentTemplateDeletePathVarsSchema = z.object({
  id: z.string()
});
